import lodash from 'lodash';
import moment from 'moment';

import { queryGraphQL } from 'common/util/query';
import { node, nodeWithArgs, toQuery, toMutation } from 'common/util/queryHelper';
import { addApplicationMessage, addApplicationMessages } from 'common/actions/applicationMessagesActions';
import { binderInstitutionIdSelector } from 'common/selectors';
import { studyNodeWrapper } from 'common/util/studyQueryHelper';

export const STUDY_EVENTS_ACTIVE_EVENT_LOADED = 'STUDY_EVENTS_ACTIVE_EVENT_LOADED';
export const STUDY_EVENTS_ACTIVE_EVENT_LOAD_END = 'STUDY_EVENTS_ACTIVE_EVENT_LOAD_END';
export const STUDY_EVENTS_ACTIVE_EVENT_LOAD_HISTORY = 'STUDY_EVENTS_ACTIVE_EVENT_LOAD_HISTORY';
export const STUDY_EVENTS_ACTIVE_EVENT_LOAD_START = 'STUDY_EVENTS_ACTIVE_EVENT_LOAD_START';
export const STUDY_EVENTS_ACTIVE_EVENT_SELECTED = 'STUDY_EVENTS_ACTIVE_EVENT_SELECTED';
export const STUDY_EVENTS_ACTIVE_EVENT_SET = 'STUDY_EVENTS_ACTIVE_EVENT_SET';
export const STUDY_EVENTS_BINDER_EVENTS_LOAD = 'STUDY_EVENTS_BINDER_EVENTS_LOAD';
export const STUDY_EVENTS_BINDER_EVENTS_LOAD_END = 'STUDY_EVENTS_BINDER_EVENTS_LOAD_END';
export const STUDY_EVENTS_BINDER_EVENTS_LOAD_START = 'STUDY_EVENTS_BINDER_EVENTS_LOAD_START';
export const STUDY_EVENTS_BINDER_USERS_LOAD = 'STUDY_EVENTS_BINDER_USERS_LOAD';
export const STUDY_EVENTS_ERROR = 'STUDY_EVENTS_ERROR';
export const STUDY_EVENTS_EVENT_CREATE = 'STUDY_EVENTS_EVENT_CREATE';
export const STUDY_EVENTS_EVENT_TYPES_SET = 'STUDY_EVENTS_EVENT_TYPES_SET';
export const STUDY_EVENTS_EVENT_UPDATE = 'STUDY_EVENTS_EVENT_UPDATE';
export const STUDY_EVENTS_EXPORT_BEGIN = 'STUDY_EVENTS_EXPORT_BEGIN';
export const STUDY_EVENTS_EXPORT_COMPLETE = 'STUDY_EVENTS_EXPORT_COMPLETE';
export const STUDY_EVENTS_UNLOAD = 'STUDY_EVENTS_UNLOAD';
export const STUDY_EVENTS_ACTIVE_EVENT_TYPE_SET = 'STUDY_EVENTS_ACTIVE_EVENT_TYPE_SET';
export const STUDY_EVENTS_GET_ICF_LANGUAGES = 'STUDY_EVENTS_GET_ICF_LANGUAGES';

// Action for getting all event types currently on display in a study.
export const STUDY_EVENTS_ACTIVE_EVENT_TYPES = 'STUDY_EVENTS_ACTIVE_EVENT_TYPES';

function theAction(type, payload = null) {
    return { type, payload };
}

export function selectActiveEvent(eventId) {
    return {
        type: STUDY_EVENTS_ACTIVE_EVENT_SELECTED,
        payload: eventId,
    };
}

export function setActiveEventType(eventType) {
    return {
        type: STUDY_EVENTS_ACTIVE_EVENT_TYPE_SET,
        payload: eventType,
    };
}

export function activeEventLoadStart() {
    return async (dispatch, getState) => {
        dispatch({
            type: STUDY_EVENTS_ACTIVE_EVENT_LOAD_START,
            payload: true,
        });
    };
}

export function activeEventLoadEnd() {
    return async (dispatch, getState) => {
        dispatch({
            type: STUDY_EVENTS_ACTIVE_EVENT_LOAD_END,
            payload: false,
        });
    };
}

export function loadActiveEvent(eventId) {
    return async (dispatch, getState) => {
        const studyId = lodash.get(getState(), 'study.id', null);
        const loadActiveEventNode = studyNodeWrapper(
            studyId,
            'studyEvent',
            nodeWithArgs('event', {
                eventId,
            }, [
                'id',
                'institutionId',
                'binderId',
                'eventType',
                'eventTypeId',
                'primaryDate',
                'monitorVisitType',
                'dateOfVisit',
                'irbApprovalDate',
                'icfType',
                'icfLanguage',
                'reconsentRequired',
                'sponsorVersion',
                'siteVersion',
                'protocolVersion',
                'summaryOfChanges',
                'signatureRequestCount',
                'signatureRequestPending',
                'signatureRequestCanceled',
                node('monitor', [
                    'id',
                    'displayName',
                ]),
                'createdAt',
                node('createdBy', [
                    'id',
                    'displayName',
                ]),
                'updatedAt',
                node('updatedBy', [
                    'id',
                    'displayName',
                ]),
                node('signatureRequests', [
                    'id',
                    node('createdBy', [
                        'id',
                        'displayName',
                        'emailAddress',
                    ]),
                    'status',
                    node('requestedFrom', [
                        'id',
                        'displayName',
                        'emailAddress',
                    ]),
                ]),
            ])
        );

        dispatch(theAction(STUDY_EVENTS_ACTIVE_EVENT_SELECTED, eventId));
        dispatch(theAction(STUDY_EVENTS_ACTIVE_EVENT_LOAD_START));
        const {
            errors,
            data,
        } = await queryGraphQL(toQuery(loadActiveEventNode));
        if (errors) {
            dispatch(addApplicationMessage('error', 'Failed to load active event'));
            dispatch(theAction(STUDY_EVENTS_ACTIVE_EVENT_LOAD_END));
            dispatch(theAction(STUDY_EVENTS_ERROR, errors));
        }
        else {
            dispatch(setActiveEvent(data[loadActiveEventNode.name]));
            dispatch(theAction(STUDY_EVENTS_ACTIVE_EVENT_LOAD_END));
        }
    };
}

export function loadActiveCustomEvent(eventId, institutionId) {
    return async (dispatch, getState) => {
        const studyId = lodash.get(getState(), 'study.id', null);
        const loadActiveCustomEvent = studyNodeWrapper(
            studyId,
            'studyCustomEvent',
            nodeWithArgs('customEvent', {
                eventId,
                institutionId,
            }, [
                'id',
                node('eventType', [
                    'id',
                    'displayName',
                    'name',
                    'slatFields',
                    'primaryDateField',
                ]),
                node('primaryDate', [
                    'name',
                    'displayName',
                    'value',
                ]),
                node('fields', [
                    'id',
                    'name',
                    'displayName',
                    'valueType',
                    'view',
                    'value',
                    'enableOtherOption',
                ]),
                node('signatureRequestStatus', [
                    'total',
                    'pending',
                    'canceled',
                ]),
                node('signatureRequests', [
                    'id',
                    node('createdBy', [
                        'id',
                        'displayName',
                        'emailAddress',
                    ]),
                    'status',
                    node('requestedFrom', [
                        'id',
                        'displayName',
                        'emailAddress',
                    ]),
                ]),
            ])
        );

        dispatch(theAction(STUDY_EVENTS_ACTIVE_EVENT_SELECTED, eventId));
        dispatch(theAction(STUDY_EVENTS_ACTIVE_EVENT_LOAD_START));
        const {
            errors,
            data,
        } = await queryGraphQL(toQuery(loadActiveCustomEvent));
        if (errors) {
            dispatch(addApplicationMessage('error', 'Failed to load active event'));
            dispatch(theAction(STUDY_EVENTS_ACTIVE_EVENT_LOAD_END));
            dispatch(theAction(STUDY_EVENTS_ERROR, errors));
        }
        else {
            dispatch(setActiveEvent(data[loadActiveCustomEvent.name]));
            dispatch(theAction(STUDY_EVENTS_ACTIVE_EVENT_LOAD_END));
        }
    };
}

export function loadEvents() {
    return (dispatch, getState) => {
        const state = getState();
        const studyId = lodash.get(state, 'study.id', null);
        const binderId = lodash.get(state, 'binder.id', null);
        dispatch(studyId ? loadStudyEvents(studyId) : loadBinderEvents(binderId));
    };
}

export function loadStudyEvents(studyId) {
    const loadStudyEventsQuery = toQuery(
        nodeWithArgs('study', {
            studyId,
        }, [
            'id',
            'name',
            node('events', [
                'id',
                'institutionId',
                'binderId',
                'eventType',
                'eventTypeId',
                'primaryDate',
                'monitorVisitType',
                'dateOfVisit',
                'irbApprovalDate',
                'icfType',
                'icfLanguage',
                'reconsentRequired',
                'sponsorVersion',
                'siteVersion',
                'protocolVersion',
                'summaryOfChanges',
                'signatureRequestCount',
                'signatureRequestPending',
                'signatureRequestCanceled',
                node('monitor', [
                    'id',
                    'displayName',
                ]),
                'createdAt',
                node('createdBy', [
                    'id',
                    'displayName',
                ]),
                'updatedAt',
                node('updatedBy', [
                    'id',
                    'displayName',
                ]),
            ]),
            node('customEvents', [
                'id',
                node('eventType', [
                    'id',
                    'displayName',
                    'name',
                    'slatFields',
                    'primaryDateField',
                ]),
                node('primaryDate', [
                    'name',
                    'displayName',
                    'value',
                ]),
                node('fields', [
                    'id',
                    'name',
                    'displayName',
                    'valueType',
                    'view',
                    'value',
                ]),
                node('signatureRequestStatus', [
                    'total',
                    'pending',
                    'canceled',
                ]),
            ]),
        ]));

    return async dispatch => {
        dispatch(theAction(STUDY_EVENTS_BINDER_EVENTS_LOAD_START));
        const {
            errors,
            data,
        } = await queryGraphQL(loadStudyEventsQuery);
        if (errors) {
            dispatch(addApplicationMessage('error', 'Failed to load study events for this study'));
            dispatch(theAction(STUDY_EVENTS_BINDER_EVENTS_LOAD_END));
            dispatch(theAction(STUDY_EVENTS_ERROR, errors));
        }
        else {
            const {
                study: {
                    events,
                    customEvents,
                }
            } = data;

            dispatch(theAction(STUDY_EVENTS_BINDER_EVENTS_LOAD, events.concat(customEvents)));
            dispatch(theAction(STUDY_EVENTS_BINDER_EVENTS_LOAD_END));
        }
    };
}

export function loadActiveEventType(institutionId, eventTypeId) {
    return async (dispatch, getState) => {
        const studyId = lodash.get(getState(), 'study.id', null);
        const eventTypeNode = studyNodeWrapper(
            studyId,
            'studyCustomEventType',
            nodeWithArgs('customEventType', {
                eventTypeId,
                institutionId,
            }, [
                'id',
                'name',
                'displayName',
                'description',
                'slatFields',
                node('fields', [
                    'name',
                    'view',
                    'displayName',
                    'valueType',
                    'enableOtherOption',
                    node('options', [
                        'type',
                        'source',
                        'values',
                    ]),
                ]),
            ])
        );

        const { errors, data } = await queryGraphQL(toQuery(eventTypeNode));
        if (errors) {
            dispatch(addApplicationMessage('error', lodash.map(errors, 'message')));
            return;
        }
        dispatch(theAction(STUDY_EVENTS_ACTIVE_EVENT_TYPE_SET, data[eventTypeNode.name]));
    };
}

export function loadBinderEvents(binderId) {
    const loadBinderEventsQuery = toQuery(
        nodeWithArgs('binder', {
            id: binderId,
        }, [
            node('events', [
                'id',
                'institutionId',
                'binderId',
                'eventType',
                'eventTypeId',
                'primaryDate',
                'monitorVisitType',
                'dateOfVisit',
                'irbApprovalDate',
                'icfType',
                'icfLanguage',
                'reconsentRequired',
                'sponsorVersion',
                'siteVersion',
                'protocolVersion',
                'summaryOfChanges',
                'signatureRequestCount',
                'signatureRequestPending',
                node('monitor', [
                    'id',
                    'displayName',
                ]),
                'createdAt',
                node('createdBy', [
                    'id',
                    'displayName',
                ]),
                'updatedAt',
                node('updatedBy', [
                    'id',
                    'displayName',
                ]),
            ]),
            node('customEvents', [
                'id',
                node('eventType', [
                    'id',
                    'displayName',
                    'name',
                    'slatFields',
                    'primaryDateField',
                ]),
                node('primaryDate', [
                    'name',
                    'displayName',
                    'value',
                ]),
                node('fields', [
                    'id',
                    'name',
                    'displayName',
                    'valueType',
                    'view',
                    'value',
                ]),
                node('signatureRequestStatus', [
                    'total',
                    'pending',
                ]),
            ]),
        ]));

    return async dispatch => {
        dispatch(theAction(STUDY_EVENTS_BINDER_EVENTS_LOAD_START));
        const {
            errors,
            data,
        } = await queryGraphQL(loadBinderEventsQuery);
        if (errors) {
            dispatch(addApplicationMessage('error', 'Failed to load binder events for this binder'));
            dispatch(theAction(STUDY_EVENTS_BINDER_EVENTS_LOAD_END));
            dispatch(theAction(STUDY_EVENTS_ERROR, errors));
        }
        else {
            const {
                binder: {
                    events,
                    customEvents,
                }
            } = data;

            dispatch(theAction(STUDY_EVENTS_BINDER_EVENTS_LOAD, events.concat(customEvents)));
            dispatch(theAction(STUDY_EVENTS_BINDER_EVENTS_LOAD_END));
        }
    };
}

export function deleteEvent() {
    return async (dispatch, getState) => {
        const state = getState();
        const { selectedEventId: eventId } = state.studyEvents;
        const {id: binderId} = state.binder;
        const { id: institutionId} = state.me.institution;
        const deleteEventNode = studyNodeWrapper(
            null,
            'deleteEvent',
            nodeWithArgs('deleteEvent', {
                eventId,
            })
        );

        const { error } = await queryGraphQL(toMutation(deleteEventNode));

        if (error) {
            dispatch(addApplicationMessage('error', 'Failed to delete event'));
        } else {
            dispatch(getEvents(institutionId, binderId));
        }
    };
}

export function createEvent(eventType, {
    institutionId,
    binderId,
    monitorVisitType,
    dateOfVisit,
    monitorId,
    monitorName,
    monitorEmailAddress,
    irbApprovalDate,
    icfType,
    icfLanguage,
    reconsentRequired,
    sponsorVersion,
    siteVersion,
    protocolVersion,
    summaryOfChanges,
    signatureRequests,
}) {
    const editNote = null;
    return async (dispatch, getState) => {
        const studyId = lodash.get(getState(), 'study.id', null);
        const createEventNode = studyNodeWrapper(
            studyId,
            'createStudyEvent',
            nodeWithArgs('createEvent', {
                input: {
                    institutionId,
                    binderId,
                    ...(studyId && { studyId }),
                    editNote,
                    eventType,
                    monitorVisitType,
                    dateOfVisit: dateOfVisit && moment(dateOfVisit).format(),
                    monitorId,
                    monitorName,
                    monitorEmailAddress,
                    irbApprovalDate: irbApprovalDate && moment(irbApprovalDate).format(),
                    icfType,
                    icfLanguage,
                    reconsentRequired,
                    sponsorVersion,
                    siteVersion,
                    protocolVersion,
                    summaryOfChanges,
                    signatureRequests,
                }
            }, [
                node('event', [
                    'id',
                    'institutionId',
                    'binderId',
                    'eventType',
                    'eventTypeId',
                    'monitorVisitType',
                    'dateOfVisit',
                    node('monitor', [
                        'id',
                        'emailAddress',
                        'displayName',
                    ]),
                    'irbApprovalDate',
                    'icfType',
                    'icfLanguage',
                    'reconsentRequired',
                    'sponsorVersion',
                    'siteVersion',
                    'protocolVersion',
                    'summaryOfChanges',
                    'createdAt',
                    node('createdBy', [
                        'id',
                        'displayName',
                    ]),
                    'updatedAt',
                    node('updatedBy', [
                        'id',
                        'displayName',
                    ]),
                ])
            ])
        );
        const {
            errors,
            data,
        } = await queryGraphQL(toMutation(createEventNode));
        if (errors) {
            dispatch(addApplicationMessage('error', 'Failed to create event'));
            dispatch(theAction(STUDY_EVENTS_ERROR, errors));
        }
        else {
            const createdEvent = data[createEventNode.name];
            dispatch(createEventAction(eventType, createdEvent.event));
            dispatch(addApplicationMessage('success', 'Event created'));
            dispatch(studyId ? loadStudyEvents(studyId) : loadBinderEvents(binderId));
        }

    };
}

function createEventAction(eventType, details = {}) {
    return {
        type: STUDY_EVENTS_EVENT_CREATE,
        payload: {
            eventType,
            details,
        },
    };
}

export function createCustomEvent(eventType, fullDetails, eventDetails) {
    const {
        institutionId,
        binderId,
        signatureRequests
    } = fullDetails;

    const details = Object.keys(eventDetails).map(name => {
        return {
            name: name,
            value: eventDetails[name],
        };
    });

    const editNote = null;
    return async (dispatch, getState) => {
        const studyId = lodash.get(getState(), 'study.id', null);
        const createCustomEventNode = studyNodeWrapper(
            studyId,
            'createStudyCustomEvent',
            nodeWithArgs('createCustomEvent', {
                input: {
                    institutionId,
                    binderId,
                    eventType,
                    details,
                    editNote,
                    signatureRequests,
                }
            }, [
                'id'
            ])
        );
        const {
            errors,
            data,
        } = await queryGraphQL(toMutation(createCustomEventNode));

        if (errors) {
            dispatch(addApplicationMessage('error', 'Failed to create event'));
            dispatch(theAction(STUDY_EVENTS_ERROR, errors));
        } else {
            const createCustomEvent = data[createCustomEventNode.name];
            dispatch(createEventAction(eventType, createCustomEvent.customEvent));
            dispatch(addApplicationMessage('success', 'Event created'));
            dispatch(studyId ? loadStudyEvents(studyId) : loadBinderEvents(binderId));
        }
    };
}

export function updateEvent(eventType, {
    id,
    institutionId,
    binderId,
    monitorVisitType,
    dateOfVisit,
    monitorId,
    monitorName,
    monitorEmailAddress,
    irbApprovalDate,
    icfType,
    icfLanguage,
    reconsentRequired,
    sponsorVersion,
    siteVersion,
    protocolVersion,
    summaryOfChanges,
    signatureRequests,
    signatureRequestsToCancel,
    editNote,
}) {
    return async (dispatch, getState) => {
        const studyId = lodash.get(getState(), 'study.id', null);
        const updateMonitorVisitEventNode = studyNodeWrapper(
            studyId,
            'updateStudyEvent',
            nodeWithArgs('updateEvent', {
                input: {
                    id,
                    institutionId,
                    editNote,
                    monitorVisitType,
                    dateOfVisit: dateOfVisit && moment(dateOfVisit).format(),
                    monitorId,
                    monitorName,
                    monitorEmailAddress,
                    irbApprovalDate: irbApprovalDate && moment(irbApprovalDate).format(),
                    icfType,
                    icfLanguage,
                    reconsentRequired,
                    sponsorVersion,
                    siteVersion,
                    protocolVersion,
                    summaryOfChanges,
                    signatureRequests,
                    signatureRequestsToCancel,
                }
            }, [
                node('event', [
                    'id',
                    'institutionId',
                    'binderId',
                    'eventType',
                    'eventTypeId',
                    'monitorVisitType',
                    'dateOfVisit',
                    node('monitor', [
                        'id',
                        'emailAddress',
                        'displayName',
                    ]),
                    'createdAt',
                    node('createdBy', [
                        'id',
                        'displayName',
                    ]),
                    'updatedAt',
                    node('updatedBy', [
                        'id',
                        'displayName',
                    ]),
                ])
            ])
        );

        const {
            errors,
            data,
        } = await queryGraphQL(toMutation(updateMonitorVisitEventNode));

        if (errors) {
            dispatch(addApplicationMessage('error', 'Failed to update event'));
            dispatch(theAction(STUDY_EVENTS_ERROR, errors));
        }
        else {
            const updateEvent = data[updateMonitorVisitEventNode.name];
            dispatch(updateEventAction(eventType, updateEvent.event));
            dispatch(addApplicationMessage('success', 'Event updated'));
        }
        dispatch(loadBinderEvents(binderId));
    };
}

export function updateCustomEvent(institutionId, binderId, eventType, details) {
    const updateableFields = details.fields.map(field => field.name);
    const updatedFields = lodash.pick(details, updateableFields);
    const fieldsNamesThatHaveChanged = Object.keys(updatedFields);

    fieldsNamesThatHaveChanged.map(updatedFieldName => {
        const updatedFieldValue = updatedFields[updatedFieldName];
        const fieldToUpdateIndex = details.fields.findIndex(field => field.name === updatedFieldName);
        details.fields[fieldToUpdateIndex].value = updatedFieldValue;
    });

    return async (dispatch, getState) => {
        const studyId = lodash.get(getState(), 'study.id', null);
        const updateCustomEventNode = studyNodeWrapper(
            studyId,
            'updateStudyCustomEvent',
            nodeWithArgs('updateCustomEvent', {
                input: {
                    id: details.id,
                    institutionId: institutionId,
                    binderId: binderId,
                    eventType: eventType,
                    fields: details.fields,
                    signatureRequests: details.signatureRequests,
                    signatureRequestsToCancel: details.signatureRequestsToCancel,
                }
            }, [
                'id',
                'institutionId',
                'binderId',
                'createdAt',
                node('createdBy', [
                    'id',
                    'displayName',
                ]),
                'updatedAt',
                node('updatedBy', [
                    'id',
                    'displayName',
                ]),
            ])
        );

        const {
            errors,
            data,
        } = await queryGraphQL(toMutation(updateCustomEventNode));

        if (errors) {
            dispatch(addApplicationMessage('error', 'Failed to update event'));
            dispatch(theAction(STUDY_EVENTS_ERROR, errors));
        } else {
            dispatch(updateEventAction(eventType, data[updateCustomEventNode.name]));
            dispatch(addApplicationMessage('success', 'Event updated'));
        }
        dispatch(loadBinderEvents(binderId));
    };
}

function updateEventAction(eventType, details = {}) {
    return {
        type: STUDY_EVENTS_EVENT_UPDATE,
        payload: {
            eventType,
            details,
        },
    };
}

export function getEventHistory(eventId) {
    return async (dispatch, getState) => {
        const studyId = lodash.get(getState(), 'study.id', null);
        const eventHistoryNode = studyNodeWrapper(
            studyId,
            'studyEventHistory',
            nodeWithArgs('eventHistory', {
                eventId
            }, [
                'timestamp',
                'actor',
                'type',
                node('data', [
                    'name',
                    'value',
                ]),
                'editNote',
            ])
        );

        const {
            errors,
            data,
        } = await queryGraphQL(toQuery(eventHistoryNode));
        if (errors) {
            dispatch(addApplicationMessage('error', 'Failed to retrieve event history'));
            dispatch(theAction(STUDY_EVENTS_ERROR, errors));
        }
        else {
            dispatch(theAction(STUDY_EVENTS_ACTIVE_EVENT_LOAD_HISTORY, data[eventHistoryNode.name]));
        }
    };
}

export function loadBinderUsers(institutionId, binderId) {
    return async (dispatch, getState) => {
        const studyId = lodash.get(getState(), 'study.id', null);
        const monitorsNode = studyNodeWrapper(
            studyId,
            'studyEventMonitors',
            nodeWithArgs('monitors', {
                institutionId,
                binderId,
            }, [
                'id',
                'sub',
                'emailAddress',
                'displayName',
            ]
            )
        );

        const loadBinderUsers = nodeWithArgs('binder', {
            id: binderId,
        }, [
            nodeWithArgs('users', { isActive: true }, [
                'id',
                'sub',
                'emailAddress',
                'displayName',
            ])
        ]);

        const query = toQuery([monitorsNode, loadBinderUsers]);

        const {
            errors,
            data,
        } = await queryGraphQL(query);

        if (errors) {
            dispatch(addApplicationMessage('error', 'Failed to load users for signature request'));
            dispatch(theAction(STUDY_EVENTS_ERROR, errors));
        }
        else {
            const {
                binder: {
                    users: internalUsers,
                }
            } = data;
            dispatch(loadBinderUsersAction(data[monitorsNode.name], internalUsers));
        }

    };

}

function loadBinderUsersAction(monitors, internalUsers) {
    return {
        type: STUDY_EVENTS_BINDER_USERS_LOAD,
        payload: {
            monitors,
            internalUsers
        }
    };
}

export function clearEvents() {
    return {
        type: STUDY_EVENTS_UNLOAD,
    };
}

export function setActiveEvent(activeEvent) {
    return {
        type: STUDY_EVENTS_ACTIVE_EVENT_SET,
        payload: activeEvent,
    };
}

export function clearActiveEvent() {
    return setActiveEvent({});
}

export function exportAllEvents(binderId, institutionId, eventTypeId) {
    return async (dispatch, getState) => {
        const studyId = lodash.get(getState(), 'study.id', null);
        const exportEventsNode = studyNodeWrapper(
            studyId,
            'studyEventsExport',
            nodeWithArgs('binderEventsExport', {
                binderId,
                institutionId,
                eventTypeId,
            }, [
                'downloadUrl',
            ])
        );
        dispatch(addApplicationMessage('info', 'Export will begin in a moment'));
        dispatch(theAction(STUDY_EVENTS_EXPORT_BEGIN));
        const {
            errors,
            data,
        } = await queryGraphQL(toQuery(exportEventsNode));
        const { downloadUrl } = data[exportEventsNode.name];
        if (errors) {
            dispatch(addApplicationMessages('error', errors.map(e => e.message)));
            dispatch(theAction(STUDY_EVENTS_ERROR, errors));
        }
        else if (downloadUrl) {
            dispatch(addApplicationMessages('download', [downloadUrl], { ttl: 0 }));
        }
        else {
            dispatch(addApplicationMessages('error', ['An error occurred with your download request. Please try again later.']));
        }
        dispatch(theAction(STUDY_EVENTS_EXPORT_COMPLETE));
    };
}

export function loadEventTypes(institutionId) {
    return async (dispatch, getState) => {
        const studyId = lodash.get(getState(), 'study.id', null);
        const eventTypesNode = studyNodeWrapper(
            studyId,
            'studyEventTypes',
            nodeWithArgs('allEventTypes', {
                institutionId,
            }, [
                'id',
                'name',
                'displayName',
                'isEnabled',
            ]));

        const {
            errors,
            data,
        } = await queryGraphQL(toQuery(eventTypesNode));
        if (errors) {
            dispatch(addApplicationMessage('error', 'Failed to load event types'));
            dispatch(theAction(STUDY_EVENTS_ERROR, errors));
        }
        else {
            dispatch(theAction(STUDY_EVENTS_EVENT_TYPES_SET, data[eventTypesNode.name]));
        }
    };
}

export function loadActiveEventTypes({ institutionId, studyId, binderId }) {
    return async (dispatch, getState) => {
        const eventTypesNode =
            nodeWithArgs('studyActiveEventTypes', {
                studyId,
                binderId,
                institutionId,
            }, [
                'id',
                'name',
                'displayName'
            ]);

        const {
            errors,
            data,
        } = await queryGraphQL(toQuery(eventTypesNode));
        if (errors) {
            dispatch(addApplicationMessage('error', 'Failed to load event types'));
            dispatch(theAction(STUDY_EVENTS_ERROR, errors));
        }
        else {
            dispatch(theAction(STUDY_EVENTS_ACTIVE_EVENT_TYPES, data[eventTypesNode.name]));
        }
    };
}


export function getICFLanguagesDataSource() {
    return async (dispatch, getState) => {
        const storeState = getState();
        const institutionId = binderInstitutionIdSelector(storeState);
        const studyId = lodash.get(storeState, 'study.id', null);
        const dataSourceNode = studyNodeWrapper(
            studyId,
            'studyLanguagesDataSources',
            nodeWithArgs('getDataSourcesForInstitution', {
                institutionId,
                query: 'Languages'
            }, [
                'id',
                'name',
                'type',
                'description',
                'institutionId',
                'parameters'
            ])
        );
        const results = await queryGraphQL(toQuery(dataSourceNode));

        if (results.errors) {
            dispatch(addApplicationMessage('error', 'Failed to get ICF Languages'));
        } else {
            dispatch({
                type: STUDY_EVENTS_GET_ICF_LANGUAGES,
                payload: results.data[dataSourceNode.name],
            });
        }
    };
}


export function getEvents(institutionId, binderId) {
    return async (dispatch) => {
        dispatch(loadEvents());
        dispatch(loadBinderUsers(institutionId, binderId));
        dispatch(loadEventTypes(institutionId));
        dispatch(getICFLanguagesDataSource());
    };
}
